import type { HeldEmail } from '@/shell/api/queries'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useId, useState } from 'react'
import { Dialog, Modal, ModalOverlay } from 'react-aria-components'
import { Link } from 'react-router'
import { Button } from '@/components/base/buttons/button'
import { Input } from '@/components/base/input/input'
import { Select } from '@/components/base/select/select'
import { api, ApiError } from '@/shell/api/client'
import { heldEmailsKey, keys, useHeldEmails } from '@/shell/api/queries'
import { Block, BlockError, Empty, Skeleton } from '@/shell/components/page'
import { toast } from '@/shell/components/toast'
import { fmtDateTime } from '@/shell/format'
import { useShell } from '@/shell/store'
import { cx } from '@/utils/cx'

/**
 * PRD-029 §3.7 / PRD-016 §8.6 acceptance 9: the Held emails section of My work, between Waiting on you and Tasks.
 * One row per held email the viewer may see (RLS: matched mail follows its client; unmatched mail is CFO/Controller).
 * Make a work item opens a small dialog prefilled from the row; Dismiss asks for a reason. Amount and holding agent
 * (PRD-016 §7.7) wait for the Email triage skill and are not shown empty.
 */
const DISMISS_REASONS = ['spam', 'duplicate', 'not for us', 'other'] as const

export function HeldEmailsBlock() {
  const { bootstrap } = useShell()
  const canSeeDismissed = bootstrap.me.role === 'cfo' || bootstrap.me.role === 'controller'
  const [showDismissed, setShowDismissed] = useState(false)
  const held = useHeldEmails('held')
  const dismissed = useHeldEmails('dismissed')
  const q = showDismissed ? dismissed : held
  const [converting, setConverting] = useState<HeldEmail | null>(null)
  const [dismissing, setDismissing] = useState<HeldEmail | null>(null)
  const isViewer = bootstrap.me.role === 'viewer'
  return (
    <Block
      title={`Held emails${held.data ? ` · ${held.data.count}` : ''}`}
      span={12}
      testId="held-emails"
      footer={canSeeDismissed
        ? <Button size="sm" color="link-color" className="min-h-6" onClick={() => setShowDismissed(v => !v)} data-testid="held-emails-toggle-dismissed">{showDismissed ? 'Show held' : 'Show dismissed (90 days)'}</Button>
        : undefined}
    >
      {q.isPending
        ? <Skeleton rows={2} />
        : q.isError
          ? <BlockError message="Could not load held emails." onRetry={() => q.refetch()} />
          : q.data.emails.length === 0
            ? <Empty>{showDismissed ? 'Nothing dismissed in the last 90 days.' : `Nothing held. Mail to tasks@${bootstrap.account.slug}.gallopaccounting.ai from a client contact lands here until someone makes it a work item.`}</Empty>
            : (
                <ul className="divide-y divide-secondary" data-testid="held-email-rows">
                  {q.data.emails.map(e => (
                    <li key={e.id} className="flex items-center gap-4 py-2 text-sm" data-testid="held-email-row" data-match={e.match_basis}>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-primary" data-testid="held-email-subject">{e.subject || '(no subject)'}</span>
                        <span className="block truncate text-xs text-quaternary">
                          {e.from_name ? `${e.from_name} <${e.from_address}>` : e.from_address}
                          {' · '}
                          {e.client_name
                            ? (
                                <span className="text-tertiary">
                                  {e.client_name}
                                  {e.file_name ? ` · ${e.file_name}` : ''}
                                </span>
                              )
                            : e.match_basis === 'ambiguous'
                              ? <span className="text-warning-primary">{`${e.match_candidates.length} possible clients`}</span>
                              : e.match_basis === 'seat'
                                ? <span className="text-tertiary">{`from ${e.sender_name ?? 'a team member'}`}</span>
                                : <span className="text-warning-primary">Unmatched sender</span>}
                          {e.attachment_count > 0 ? ` · ${e.attachment_count} ${e.attachment_count === 1 ? 'attachment' : 'attachments'}` : ''}
                          {e.body_status === 'unavailable' ? ' · subject only' : ''}
                          {showDismissed && e.dismissed_reason ? ` · dismissed: ${e.dismissed_reason}${e.acted_by_name ? ` by ${e.acted_by_name}` : ''}` : ''}
                        </span>
                      </span>
                      <span className="w-32 text-right text-xs tabular-nums text-quaternary"><time dateTime={e.received_at}>{fmtDateTime(e.received_at)}</time></span>
                      {!isViewer
                        ? (
                            <span className="flex shrink-0 gap-2">
                              <Button size="sm" color="secondary" onClick={() => setConverting(e)} data-testid="held-email-convert">Make a work item</Button>
                              {!showDismissed ? <Button size="sm" color="link-gray" onClick={() => setDismissing(e)} data-testid="held-email-dismiss">Dismiss</Button> : null}
                            </span>
                          )
                        : null}
                    </li>
                  ))}
                </ul>
              )}
      {converting ? <ConvertDialog email={converting} onClose={() => setConverting(null)} /> : null}
      {dismissing ? <DismissDialog email={dismissing} onClose={() => setDismissing(null)} /> : null}
    </Block>
  )
}

function useRefreshHeld() {
  const qc = useQueryClient()
  return () => {
    void qc.invalidateQueries({ queryKey: heldEmailsKey('held') })
    void qc.invalidateQueries({ queryKey: heldEmailsKey('dismissed') })
    void qc.invalidateQueries({ queryKey: keys.myWork })
    void qc.invalidateQueries({ queryKey: keys.bootstrap })
  }
}

function ConvertDialog({ email, onClose }: { email: HeldEmail, onClose: () => void }) {
  const { bootstrap } = useShell()
  const titleId = useId()
  const refresh = useRefreshHeld()
  const candidateIds = new Set(email.match_candidates.map(c => c.id))
  const clients = bootstrap.tree.map(t => ({ id: t.cluster.id, label: t.cluster.name, files: t.files }))
  const [clusterId, setClusterId] = useState<string | null>(email.client_id ?? (email.match_candidates.length === 1 ? email.match_candidates[0]!.id : null))
  const [fileId, setFileId] = useState<string | null>(email.file_id)
  const [title, setTitle] = useState(email.subject || 'Email')
  const [result, setResult] = useState<{ workItemId: string, attached: number, refused: Array<{ name: string, reason: string }> } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const node = clients.find(c => c.id === clusterId)
  const convert = useMutation({
    mutationFn: () => api<{ workItemId: string, attached: number, refused: Array<{ name: string, reason: string }> }>(`/held-emails/${email.id}/convert`, { method: 'POST', json: { title: title.trim(), kind: 'task', clusterId, fileId } }),
    onSuccess: (r) => {
      refresh()
      setResult(r)
      toast(r.attached > 0 ? `Work item created with ${r.attached} ${r.attached === 1 ? 'attachment' : 'attachments'}.` : 'Work item created.')
    },
    onError: err => setError(err instanceof ApiError && err.body?.message ? String(err.body.message) : 'Could not create the work item.'),
  })
  // A matched row converts on its client only (the API refuses another); candidates are listed first for an ambiguous row.
  const clientItems = email.client_id
    ? clients.filter(c => c.id === email.client_id)
    : [...clients.filter(c => candidateIds.has(c.id)), ...clients.filter(c => !candidateIds.has(c.id))]
  return (
    <ModalOverlay isOpen isDismissable onOpenChange={o => !o && onClose()} className="fixed inset-0 z-50 flex items-center justify-center bg-overlay/40 p-4">
      <Modal className="w-full max-w-md rounded-xl bg-primary p-6 shadow-xl">
        <Dialog aria-labelledby={titleId} className="outline-none" data-testid="held-email-convert-dialog">
          <h2 id={titleId} className="text-lg font-semibold text-primary">Make a work item</h2>
          <p className="mt-1 truncate text-sm text-tertiary">{`From ${email.from_name ? `${email.from_name} <${email.from_address}>` : email.from_address}`}</p>
          {result
            ? (
                <div className="mt-4 flex flex-col gap-3" data-testid="held-email-converted">
                  <p className="text-sm text-primary">The task is assigned to you with a scan-gated email snapshot attached. The email is human-review-only.</p>
                  {result.refused.length > 0
                    ? (
                        <ul className="rounded-lg border border-warning bg-warning-secondary p-3 text-xs text-warning-primary">
                          {result.refused.map(r => <li key={`${r.name}:${r.reason}`}>{r.name === '*' ? r.reason : `Not attached: ${r.name} (${r.reason.replace(/_/g, ' ')})`}</li>)}
                        </ul>
                      )
                    : null}
                  <div className="flex justify-end gap-2">
                    <Button type="button" color="secondary" size="sm" onClick={onClose}>Close</Button>
                    <Link to={`/w/${result.workItemId}`} onClick={onClose} className="inline-flex h-8 items-center rounded-lg bg-brand-solid px-3 text-sm font-semibold text-primary_on-brand hover:bg-brand-solid_hover" data-testid="held-email-open-item">Open item</Link>
                  </div>
                </div>
              )
            : (
                <form
                  className="mt-4 flex flex-col gap-3"
                  onSubmit={(e) => {
                    e.preventDefault()
                    if (!clusterId || title.trim().length === 0)
                      return
                    setError(null)
                    convert.mutate()
                  }}
                >
                  <Input label="Title" autoFocus isRequired value={title} onChange={setTitle} maxLength={300} data-testid="held-email-title" />
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Select
                      label="Client"
                      size="sm"
                      items={clientItems}
                      selectedKey={clusterId}
                      isDisabled={Boolean(email.client_id)}
                      onSelectionChange={(k) => {
                        setClusterId(k === null ? null : String(k))
                        setFileId(null)
                      }}
                      data-testid="held-email-client"
                    >
                      {item => <Select.Item id={item.id} label={item.label} />}
                    </Select>
                    <Select label="File" size="sm" placeholder="Client level" items={[{ id: '', label: 'Client level' }, ...(node?.files ?? []).map(f => ({ id: f.id, label: f.name }))]} selectedKey={fileId ?? ''} onSelectionChange={k => setFileId(k === null || k === '' ? null : String(k))} isDisabled={!node} data-testid="held-email-file">
                      {item => <Select.Item id={item.id} label={item.label} />}
                    </Select>
                  </div>
                  {email.match_basis === 'ambiguous' ? <p className="text-xs text-warning-primary">{`This sender is a contact on ${email.match_candidates.length} clients; the likely ones are listed first.`}</p> : null}
                  {email.attachment_count > 0 ? <p className="text-xs text-tertiary">{`${email.attachment_count} ${email.attachment_count === 1 ? 'attachment' : 'attachments'} will be fetched and scanned before anyone can open them.`}</p> : null}
                  <p className="text-xs text-quaternary">Assigned to you, due at the file's close target for the open month. Change either on the item.</p>
                  {error ? <p role="alert" className="text-sm text-error-primary" data-testid="held-email-error">{error}</p> : null}
                  <div className="mt-2 flex justify-end gap-2">
                    <Button type="button" color="secondary" size="sm" onClick={onClose}>Cancel</Button>
                    <Button type="submit" color="primary" size="sm" isDisabled={!clusterId || title.trim().length === 0} isLoading={convert.isPending} data-testid="held-email-confirm">Create work item</Button>
                  </div>
                </form>
              )}
        </Dialog>
      </Modal>
    </ModalOverlay>
  )
}

function DismissDialog({ email, onClose }: { email: HeldEmail, onClose: () => void }) {
  const titleId = useId()
  const refresh = useRefreshHeld()
  const [reason, setReason] = useState<string>('spam')
  const [other, setOther] = useState('')
  const dismiss = useMutation({
    mutationFn: () => api(`/held-emails/${email.id}/dismiss`, { method: 'POST', json: { reason: reason === 'other' ? other.trim() || 'other' : reason } }),
    onSuccess: () => {
      refresh()
      toast('Email dismissed.')
      onClose()
    },
    onError: err => toast(err instanceof ApiError && err.body?.message ? String(err.body.message) : 'Could not dismiss.', { tone: 'error' }),
  })
  return (
    <ModalOverlay isOpen isDismissable onOpenChange={o => !o && onClose()} className="fixed inset-0 z-50 flex items-center justify-center bg-overlay/40 p-4">
      <Modal className="w-full max-w-sm rounded-xl bg-primary p-6 shadow-xl">
        <Dialog aria-labelledby={titleId} className="outline-none" data-testid="held-email-dismiss-dialog">
          <h2 id={titleId} className="text-lg font-semibold text-primary">Dismiss this email</h2>
          <p className="mt-1 truncate text-sm text-tertiary">{email.subject || '(no subject)'}</p>
          <div className="mt-4 flex flex-wrap gap-2" role="radiogroup" aria-label="Reason">
            {DISMISS_REASONS.map(r => (
              <button key={r} type="button" role="radio" aria-checked={reason === r} onClick={() => setReason(r)} className={cx('rounded-full border px-3 py-1 text-xs', reason === r ? 'border-brand bg-brand-secondary text-brand-secondary' : 'border-secondary text-tertiary hover:text-primary')} data-testid={`dismiss-reason-${r.replace(/\s/g, '-')}`}>{r}</button>
            ))}
          </div>
          {reason === 'other' ? <Input className="mt-3" size="sm" aria-label="Reason" value={other} onChange={setOther} placeholder="Why" maxLength={200} /> : null}
          <p className="mt-3 text-xs text-quaternary">Dismissed mail is kept; a CFO or Controller can still make a work item from it later.</p>
          <div className="mt-4 flex justify-end gap-2">
            <Button type="button" color="secondary" size="sm" onClick={onClose}>Cancel</Button>
            <Button type="button" color="primary-destructive" size="sm" onClick={() => dismiss.mutate()} isLoading={dismiss.isPending} data-testid="held-email-dismiss-confirm">Dismiss</Button>
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  )
}
